import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { minusWithDecimals, multiplyWithDecimals } from '../../../../../config/utils/calcUtils';
import {
  DEFAULT_SUBTOTAL_SIGN,
  getNewSubtotalUtility,
  getTotalChargesReturns,
} from '../../../../../config/utils/reportUtils';
import { CURRENCY } from '../../../../../interfaces/enumerables';
import { CustomerReportDoc, CustomerReportResponse } from '../../../../../interfaces/reports.interface';
import { DocsReportTableHeaderView } from './DocsReportTableHeaderView';
import { DocsTableListView } from './DocsTableListView';

interface Props {
  data: CustomerReportResponse;
}

export const CustomerReportView = ({ data }: Props) => {
  const { t } = useTranslation();
  const customerData = useMemo(() => data?.data?.customer, [data?.data?.customer]);
  const supplierData = useMemo(() => data?.data?.supplier, [data?.data?.supplier]);

  const getFinalSubtotals = useCallback((dataParam: CustomerReportDoc[] | undefined) => {
    const len = dataParam?.length;
    if (len && len > 0) {
      return getNewSubtotalUtility(dataParam[len - 1]);
    }
    return { rawValue: '0.00', currentPosSubtotal: '', currentNegSubtotal: '' };
  }, []);

  const finalCustomerSubtotals = useMemo(() => getFinalSubtotals(customerData), [customerData, getFinalSubtotals]);
  const finalSupplierSubtotals = useMemo(() => getFinalSubtotals(supplierData), [supplierData, getFinalSubtotals]);

  const getSubtotal = useCallback((subtotals: ReturnType<typeof getNewSubtotalUtility>, type: 'Pos' | 'Neg') => {
    if (subtotals[`current${type}Subtotal`] === DEFAULT_SUBTOTAL_SIGN) {
      return subtotals[`current${type}Subtotal`];
    }

    return `${subtotals[`current${type}Subtotal`] || '0.00'} ${CURRENCY.EUR}`;
  }, []);

  const getTotals = useCallback((dataArg: CustomerReportDoc[]) => {
    return getTotalChargesReturns(dataArg || []);
  }, []);

  const renderSection = useCallback(
    (dataArg: CustomerReportDoc[], subtotals: ReturnType<typeof getNewSubtotalUtility>, title?: string) => {
      if (dataArg?.length) {
        return (
          <div className="width-100 margin-b-16">
            {title && <div className="normalText1 center padding-l-4 padding-b-8">{title}</div>}
            <div className="width-100">
              <DocsTableListView docs={dataArg} />
            </div>
            <div className="width-100 padding-t-10 padding-r-4 padding-l-20 flex-start-center-row-nowrap">
              <div className="boldText3 width-418px">{t('CUSTOMERS_REPORT.TOTAL')}</div>
              <div className="boldText3 width-80px text-center">
                {getTotals(dataArg || [])?.charges} {CURRENCY.EUR}
              </div>
              <div className="boldText3 width-80px text-center">
                {getTotals(dataArg || [])?.returns} {CURRENCY.EUR}
              </div>
              <div className="boldText3 width-80px text-center" data-testid="charge-subtotal">
                {getSubtotal(subtotals, 'Pos')}
              </div>
              <div className="boldText3 width-80px text-center" data-testid="return-subtotal">
                {getSubtotal(subtotals, 'Neg')}
              </div>
            </div>
          </div>
        );
      }
    },
    [getSubtotal, getTotals, t],
  );

  const getFinalTotalsCombined = useCallback(() => {
    if (customerData?.length && supplierData?.length) {
      const total = minusWithDecimals(finalCustomerSubtotals.rawValue, finalSupplierSubtotals.rawValue);
      let posTotal = '0.00';
      let negTotal = '0.00';
      if (Number(total) > 0) {
        posTotal = total;
      } else {
        negTotal = multiplyWithDecimals(total, -1);
      }

      return (
        <>
          <div className="width-100 padding-t-10 padding-r-4 padding-l-20 flex-start-center-row-nowrap">
            <div className="boldText4 width-418px">{t('CUSTOMERS_REPORT.FINAL_SUBTOTALS')}</div>
            <div className="boldText3 width-80px text-center">-</div>
            <div className="boldText3 width-80px text-center">-</div>
            <div className="boldText3 width-80px text-center">
              {posTotal} {CURRENCY.EUR}
            </div>
            <div className="boldText3 width-80px text-center">
              {negTotal} {CURRENCY.EUR}
            </div>
          </div>
        </>
      );
    }
  }, [customerData?.length, finalCustomerSubtotals.rawValue, finalSupplierSubtotals.rawValue, supplierData?.length, t]);

  const getSectionTitle = useCallback(() => {
    if (customerData?.length && supplierData?.length) {
      return t('CUSTOMERS_REPORT.SUPPLIER_TRANSACTIONS');
    }

    return '';
  }, [customerData?.length, supplierData?.length, t]);

  return (
    <div className="flex-center-center-column-nowrap padding-t-10 width-100">
      <div className="width-100">
        <DocsReportTableHeaderView />
      </div>
      {renderSection(customerData, finalCustomerSubtotals)}
      {renderSection(supplierData, finalSupplierSubtotals, getSectionTitle())}
      {getFinalTotalsCombined()}
    </div>
  );
};
